import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSManualReviewData,
  type EnrollmentHYCSManualReviewSubmitInput,
  type EnrollmentHYCSManualReviewSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS ManualReview data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSManualReviewData>
 * @example const response = await getEnrollmentHYCSManualReviewData()
 */
export const getEnrollmentHYCSManualReviewData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSManualReviewData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSManualReviewData',
      query: `mutation getEnrollmentHYCSManualReviewData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSManualReviewData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSManualReviewData = createQueryHook<undefined, EnrollmentHYCSManualReviewData>(
  getEnrollmentHYCSManualReviewData,
)

/**
 * Submit Enrollment HYCS ManualReview data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSManualReviewSubmitOutput>
 * @example const response = await submitEnrollmentHYCSManualReviewData();
 */
export const submitEnrollmentHYCSManualReviewData = async (
  enrollmentManualReviewSubmitInput: EnrollmentHYCSManualReviewSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSManualReviewSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSManualReviewData',
      query: `mutation submitEnrollmentHYCSManualReviewData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentManualReviewSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSManualReviewSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSManualReviewData = createMutationHook<
  EnrollmentHYCSManualReviewSubmitInput,
  EnrollmentHYCSManualReviewSubmitOutput
>(submitEnrollmentHYCSManualReviewData)
